import { ReactNode, memo } from 'react';
import { cn } from '../../lib/utils';

const themes = {
  dark: {
    badge: 'bg-white/5 border-white/10 text-white/80',
    title: 'text-white',
    subtitle: 'text-white/60',
    dot: 'bg-gradient-to-r from-brand to-cyan-500',
  },
  light: {
    badge: 'text-blue-600',
    title: 'text-slate-900',
    subtitle: 'text-slate-500',
    dot: 'bg-gradient-to-r from-blue-500 to-cyan-500',
  },
} as const;

export interface SectionHeaderProps {
  badge?: string;
  title: ReactNode;
  subtitle?: string;
  theme?: keyof typeof themes;
  align?: 'left' | 'center';
  className?: string;
}

export const SectionHeader = memo(function SectionHeader({
  badge,
  title,
  subtitle,
  theme = 'dark',
  align = 'center',
  className,
}: SectionHeaderProps) {
  const styles = themes[theme];

  return (
    <div className={cn('mb-16 lg:mb-20', align === 'center' && 'text-center', className)}>
      {badge && (
        <div
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 backdrop-blur-sm',
            theme === 'dark' && 'border',
            styles.badge
          )}
        >
          <span className={cn('w-2 h-2 rounded-full animate-pulse', styles.dot)} />
          <span className="font-semibold text-sm uppercase tracking-widest">{badge}</span>
        </div>
      )}

      <h2
        className={cn(
          'text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-6 leading-tight',
          styles.title
        )}
      >
        {title}
      </h2>

      {subtitle && (
        <p
          className={cn(
            'text-xl md:text-2xl max-w-3xl leading-relaxed',
            align === 'center' && 'mx-auto',
            styles.subtitle
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
});

// Gradient text helper component
export interface GradientTextProps {
  children: ReactNode;
  gradient?: string;
  className?: string;
}

export const GradientText = memo(function GradientText({
  children,
  gradient = 'from-brand-400 via-cyan-400 to-cyan-300',
  className,
}: GradientTextProps) {
  return (
    <span className={cn('text-transparent bg-clip-text bg-gradient-to-r', gradient, className)}>
      {children}
    </span>
  );
});

// Section Divider with lines
export interface SectionDividerProps {
  label: string;
  className?: string;
}

export const SectionDivider = memo(function SectionDivider({
  label,
  className,
}: SectionDividerProps) {
  return (
    <div className={cn('inline-flex items-center gap-3 mb-6', className)}>
      <div className="h-px w-12 bg-gradient-to-r from-transparent to-blue-500" />
      <span className="text-sm font-bold uppercase tracking-[0.3em] text-blue-600">{label}</span>
      <div className="h-px w-12 bg-gradient-to-l from-transparent to-blue-500" />
    </div>
  );
});

